/*!
// <copyright file="MessageBoxHandling.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util } from "@ax/office-shared-lib";
import { resources } from "@ax/office-cdn-app-resources";
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { appElements } from "./Elements";
import { appState } from "./AppState";
import { notifications } from "./Notifications";
import * as $ from "jquery";
import "jquery-ui/ui/tabbable";
import "jquery-ui/ui/keycode";

export namespace messageBoxHandling {

    /* jshint -W069 */
    /** The message types displayed by the message box handler. */
    export enum MessageBoxType {

        /** Represents the confirmation message box type. */
        Confirmation = <any>"Confirmation",

        /** Represents the error message box type. */
        Error = <any>"Error",

        /** Represents the notification message box type. */
        Notification = <any>"Notification"
    }
    /* jshint +W069 */

    export interface ShowConfirmationDialogCallback { (confirm: boolean): void }

    export interface ShowConfirmationDialogOptions {

        /** The details to display. */
        Details?: string;

        /** The ID of the requesting source. */
        SourceID?: string;
    }

    interface MessageInfoOptions {

        /** The secondary text of the message. */
        Details?: string;

        /** The ID of the originating source. */
        SourceID?: string;

        /** An object containing a Text string and Action function used to display a link. */
        LinkOptions?: notifications.LinkOptions;

        /** The callback executed when the user responds to a confirmation message. */
        Callback?: ShowConfirmationDialogCallback;

        /** Indicates if the correlation ID should be displayed; false by default. */
        IncludeCorrelationID?: boolean;
    }

    /** Represents the information required by the message box handler to display a message. */
    class MessageInfo {

        /** The type of message box to display. */
        public Type: MessageBoxType;

        /** The main text of the message. */
        public Message: string;

        /** The secondary text of the message. */
        public Details: string;

        /** The ID of the originating source. */
        public SourceID: string;

        /** An object containing a Text string and Action function used to display a link. */
        public LinkOptions: notifications.LinkOptions;

        /** The callback executed when the user responds to a confirmation message. */
        public Callback: ShowConfirmationDialogCallback;

        /** Indicates if the correlation ID should be displayed; false by default. */
        public IncludeCorrelationID: boolean;

        /**
         * @param type The type of message box to display.
         * @param message The main text of the message.
         * @param options Optional information about the message.
         */
        constructor(messageType: MessageBoxType, message: string, options?: MessageInfoOptions) {
            options = options || {};
            this.Type = messageType;
            this.Message = message;
            this.Details = options.Details;
            this.SourceID = options.SourceID;
            this.LinkOptions = options.LinkOptions;
            this.Callback = options.Callback;
            this.IncludeCorrelationID = options.IncludeCorrelationID;
        }
    }

    interface HandlerShowErrorOptions {

        /** A string containing the message details. */
        Details?: string;

        /** An object containing a Text string and Action function used to display a link. */
        LinkOptions?: notifications.LinkOptions;

        /** Indicates if the correlation ID should be displayed; false by default. */
        IncludeCorrelationID?: boolean;

        /** The string that should be copied to the user's clipboard; if not present no copy button is shown. */
        CopyText?: string;
    }

    interface HandlerShowNotification {

        /** A string containing the message details. */
        Details?: string;

        /** An object containing a Text string and Action function used to display a link. */
        LinkOptions?: notifications.LinkOptions;
    }

    /** Handles error reporting and confirmation message display for the app. */
    export class MessageBoxHandler {

        /** The error container control. */
        private _messageBoxContainer: JQuery;

        /** The message box message element. */
        private _messageBoxMessage: JQuery;

        /** The message box details element. */
        private _messageBoxDetails: JQuery;

        /** The element used to display the error symbol. */
        private _errorSymbol: JQuery;

        /** The element used to display the warning symbol. */
        private _warningSymbol: JQuery;

        /** The message box label control. */
        private _labelElement: JQuery;

        /** The message box text control. */
        private _textElement: JQuery;

        /** The message box link control. */
        private _linkElement: JQuery;

        /** The text area element used to facilitate copying text. */
        private _copyTargetElement: JQuery;

        /** The OK/yes button control. */
        private _okButton: JQuery;

        /** The no button control. */
        private _noButton: JQuery;

        /** The current message being displayed. */
        private _currentMessage: MessageInfo;

        /** The stack of messages to be shown after the current message is addressed. */
        private _messageStack: MessageInfo[];

        /** The IDs of controls required by the message box handler. */
        private static ControlIDs = {

            /** ID of the message box container element. */
            container: "messageBoxContainer",

            /** ID of the message box error symbol element. */
            errorSymbol: "errorSymbol",

            /** ID of the message box warning symbol element. */
            warningSymbol: "warningSymbol",

            /** ID of the message box message element. */
            messageBoxMessage: "messageBoxMessage",

            /** ID of the message box details element. */
            messageBoxDetails: "messageBoxDetails",

            /** ID of the message box label element. */
            messagelabel: "messageBoxLabel",

            /** ID of the message box text element. */
            messageText: "messageBoxText",

            /** ID of the message box link element. */
            link: "messageBoxLink",

            /** The ID of the text area element used to facilitate copying text. */
            copyTarget: "messageBoxCopyTarget",

            /** The ID of the copy button. */
            copyButton: "messageBoxCopyButton",

            /** ID of the message box OK/yes button. */
            okButton: "messageBoxOkButton",

            /** ID of the message box no button. */
            noButton: "messageBoxNoButton"
        };

        /** The names of the attributes and attribute values required by the message box handler. */
        private static Attributes = {

            /** Name of the attribute used to control the message box type. */
            messageBoxType: "data-message-box-type",

            /** Name of the attribute value for the error message box type. */
            messageBoxType_error: "error",

            /** Name of the attribute value for the confirmation message box type. */
            messageBoxType_confirmation: "confirmation",

            /** Name of the attribute value for the notification message box type. */
            messageBoxType_notification: "notification",

            /** Name of the attribute value used to indicate if the message box has a link. */
            hasLink: "data-has-link",

            /** Name of the attribute value used to indicate if the message box has a copy button. */
            hasCopyButton: "data-has-copy",
        };

        /* jshint -W040 */
        constructor() {
            this._windowKeyDownForceMessageBoxFocus = this._windowKeyDownForceMessageBoxFocus.bind(this);
            this._okButtonClicked = this._okButtonClicked.bind(this);
            this._noButtonClicked = this._noButtonClicked.bind(this);
            this._messageStack = [];
        }
        /* jshint +W040 */

        /** Initializes the message box handler. */
        public initialize(): void {
            this._messageBoxContainer = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.container, appElements.contentDiv);
            this._messageBoxMessage = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.messageBoxMessage, this._messageBoxContainer);
            this._messageBoxDetails = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.messageBoxDetails, this._messageBoxMessage);
            this._errorSymbol = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.errorSymbol, this._messageBoxContainer);
            this._warningSymbol = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.warningSymbol, this._messageBoxContainer);
            this._labelElement = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.messagelabel, this._messageBoxContainer);
            this._textElement = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.messageText, this._messageBoxContainer);
            this._linkElement = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.link, this._messageBoxContainer);
            this._copyTargetElement = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.copyTarget, this._messageBoxContainer);
            this._okButton = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.okButton, this._messageBoxContainer);
            this._okButton.click(this._okButtonClicked);
            this._noButton = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.noButton, this._messageBoxContainer);
            this._noButton.text(resources.dynamicsApp.noButtonLabel());
            this._noButton.click(this._noButtonClicked);
            this._messageBoxMessage.keydown((event: JQuery.KeyDownEvent<HTMLElement>) => {
                if (event.which === $.ui.keyCode.TAB) {
                    // Attempt to find tabbable elements
                    let tabbableElements: JQuery;
                    try {
                        tabbableElements = this._messageBoxContainer.find(":tabbable");
                    } catch (e) {
                        tabbableElements = $();
                    }
                    let elementToRefocus: JQuery;
                    if (tabbableElements.length === 0) {
                        elementToRefocus = $(tabbableElements[0]);
                    } else if (tabbableElements.length > 0) {
                        if (event.shiftKey && event.target === tabbableElements[0]) {
                            elementToRefocus = $(tabbableElements[tabbableElements.length - 1]);
                        } else if (!event.shiftKey && event.target === tabbableElements[tabbableElements.length - 1]) {
                            elementToRefocus = $(tabbableElements[0]);
                        }
                    }
                    if (elementToRefocus) {
                        util.setFocus(elementToRefocus);
                        event.stopPropagation();
                        event.preventDefault();
                    }
                }
            });
            this._linkElement.keydown((event: JQuery.KeyDownEvent<HTMLElement>) => {
                if (event.which === $.ui.keyCode.SPACE) {
                    this._linkElement.click();
                    event.stopPropagation();
                    event.preventDefault();
                }
            });
            const copyButton = util.findElementById(messageBoxHandling.MessageBoxHandler.ControlIDs.copyButton, this._messageBoxContainer);
            if (copyButton.length) {
                copyButton.click(
                    () => {
                        try {
                            const clipboard = window.navigator && (window.navigator as any).clipboard;
                            if (clipboard && clipboard.writeText) {
                                clipboard.writeText(this._copyTargetElement.val());
                            } else {
                                this._copyTargetElement.select();
                                document.execCommand("copy");
                                copyButton.focus();
                            }
                        } catch (e) {
                            if (e && e.message) {
                                this.showError(e.message);
                            }
                        }
                    });
                util.customTooltip(copyButton[0], resources.dynamicsApp.notification_CopyButtonTitle(), "tooltipText");
            }
        }

        /**
         * Shows the provided error to the user.
         * @param message The error message to display.
         * @param options The error options to display.
         */
        public showError(message: string, options?: HandlerShowErrorOptions): void {

            if (options === undefined) {
                options = {};
            }

            // Register the new message
            this._registerNewMessage(new MessageInfo(MessageBoxType.Error, message, options));

            // Set up the symbols and buttons
            this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType, messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType_error);
            this._okButton.text(resources.dynamicsApp.okButtonLabel());

            var formattedMessage;

            // If the details are undefined just use the provided message as the formatted message;
            // otherwise use a message containing both the provided message and the provided details as the formatted message
            if (options.Details === undefined) {
                formattedMessage = message;
            } else {
                formattedMessage = resources.dynamicsApp.notification_WithDetailsMessageFormat(message, options.Details);
            }

            if (appState.State.ServerConfig && appState.State.Features.LogTelemetry && options.IncludeCorrelationID === true) {
                formattedMessage = resources.dynamicsApp.notification_WithCorrelationIdMessageFormat(formattedMessage, appState.State.CorrelationID);
            }

            // Set the text element's text to the formatted message
            this._textElement.text(formattedMessage);
            this._labelElement.text(resources.dynamicsApp.notification_ErrorMessageLabel());

            if (options.LinkOptions !== undefined) {
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "true");
                this._linkElement.text(options.LinkOptions.Text);
                this._linkElement.off("click");
                this._linkElement.click(() => {
                    options.LinkOptions.Action();
                    this._okButtonClicked();
                });
            } else {
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "false");
                this._linkElement.text("");
                this._linkElement.off("click");
            }

            const clipboard = window.navigator && (window.navigator as any).clipboard;
            if (options.CopyText && (document.execCommand || (clipboard && clipboard.writeText))) {
                this._copyTargetElement.val(options.CopyText);
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasCopyButton, "true");
            } else {
                this._copyTargetElement.val("");
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasCopyButton, "false");
            }

            this._showMessageBox();
        }

        /**
         * Displays a confirmation dialog to the user.
         * @param message The message to display.
         * @param callback The function to call with the user's response to the dialog.
         * @param options The options to adjust the showing of the confirmation dialog.
         */
        public showConfirmationDialog(message: string, callback: ShowConfirmationDialogCallback, options: ShowConfirmationDialogOptions = {}): void {

            // Register the new message
            const startTime = new Date().getTime();
            this._registerNewMessage(
                new MessageInfo(
                    MessageBoxType.Confirmation,
                    message,
                    {
                        Callback: (confirm: boolean) => {
                            telemetry.events.confirmationClosed.log(options.SourceID, confirm, new Date().getTime() - startTime);
                            callback(confirm);
                        },
                        Details: options.Details,
                        SourceID: options.SourceID
                    }));

            // Set up the symbols and buttons
            this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType, messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType_confirmation);
            this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "false");
            this._okButton.text(resources.dynamicsApp.yesButtonLabel());

            var formattedMessage;

            // If the details are undefined just use the provided message as the formatted message;
            // otherwise use a message containing both the provided message and the provided details as the formatted message
            if (options.Details === undefined) {
                formattedMessage = message;
            } else {
                formattedMessage = resources.dynamicsApp.notification_TitleAndMessageFormat(message, options.Details);
            }

            // Set the text element's text to the formatted message
            this._textElement.text(formattedMessage);
            this._labelElement.text(resources.dynamicsApp.notification_ConfirmationMessageLabel());

            this._showMessageBox();
        }

        /**
         * Shows the provided notification to the user.
         * @param message The notification message to display.
         * @param options The error options to display.
         */
        public showNotification(message: string, options?: HandlerShowNotification): void {

            if (options === undefined) {
                options = {};
            }

            // Register the new message
            this._registerNewMessage(new MessageInfo(MessageBoxType.Notification, message, options));

            // Set up the symbols and buttons
            this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType, messageBoxHandling.MessageBoxHandler.Attributes.messageBoxType_notification);
            this._okButton.text(resources.dynamicsApp.okButtonLabel());

            var formattedMessage;

            // If the details are undefined just use the provided message as the formatted message;
            // otherwise use a message containing both the provided message and the provided details as the formatted message
            if (options.Details === undefined) {
                formattedMessage = message;
            } else {
                formattedMessage = resources.dynamicsApp.notification_WithDetailsMessageFormat(message, options.Details);
            }

            // Set the text element's text to the formatted message
            this._textElement.text(formattedMessage);
            this._labelElement.text(resources.dynamicsApp.notification_NotificationMessageLabel());

            if (options.LinkOptions !== undefined) {
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "true");
                this._linkElement.text(options.LinkOptions.Text);
                this._linkElement.click(() => {
                    options.LinkOptions.Action();
                    this._okButtonClicked();
                });
            } else {
                this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "false");
                this._linkElement.text("");
                this._linkElement.off("click");
            }

            this._showMessageBox();
        }

        /** Shows the message box element, adjusting focus and event handlers appropriately to ensure focus remains in the message box. */
        public _showMessageBox(): void {
            util.changeAttribute(this._messageBoxContainer, appElements.GeneralAttributes.isShown, true);
            util.setFocus(this._messageBoxContainer);
            $(window).unbind("keydown");
            $(window).keydown(<any>this._windowKeyDownForceMessageBoxFocus);
        }

        /**
         * Event handler for when a key is pressed down within the window; ensures focus remains within the message box.
         * @param event The key down event.
         */
        public _windowKeyDownForceMessageBoxFocus(event: JQuery.KeyDownEvent<HTMLElement>): void {
            if (!$.contains(this._messageBoxMessage[0], event.target) && event.which === $.ui.keyCode.TAB) {
                util.setFocus(this._messageBoxMessage);
                event.stopPropagation();
                event.preventDefault();
            }
        }

        /**
         * Registers that a new message is being displayed and adds the current message, if one is shown, to the stack.
         * @param newMessage The new message to register.
         */
        public _registerNewMessage(newMessage: MessageInfo): void {
            if (this._currentMessage !== undefined) {
                this._messageStack.push(this._currentMessage);
            }
            this._currentMessage = newMessage;
        }

        /**
         * Event handler for when the OK button is clicked; closes the message box and resets the message text to empty.
         * @param event The OK button clicked event.
         */
        public _okButtonClicked(event?: JQuery.ClickEvent<HTMLElement>): void {
            this._registerClick(true);
        }

        /**
         * Event handler for when the no button is clicked; closes the message box and resets the message text to empty.
         * @param event The no button clicked event.
         */
        public _noButtonClicked(event: JQuery.ClickEvent<HTMLElement>): void {
            this._registerClick(false);
        }

        /**
         * Closes the message box and resets the message text to empty; if the current message has a callback, it's called with
         * the specified value. The next queued message is displayed if one exists.
         * @param confirmation Indicates whether the user confirmed (true) or canceled (false).
         */
        public _registerClick(confirmation: boolean): void {

            if (this._currentMessage !== undefined && this._currentMessage.Callback !== undefined) {
                this._currentMessage.Callback(confirmation);
            }

            // Reset the current message and all visible controls
            this._textElement.text("");
            this._labelElement.text("");
            this._messageBoxContainer.attr(messageBoxHandling.MessageBoxHandler.Attributes.hasLink, "false");
            this._linkElement.text("");
            this._linkElement.off("click");
            this._currentMessage = undefined;

            // If there is a message in the stack to be displayed, show it; otherwise, hide the container
            if (this._messageStack.length > 0) {
                var stackMessage = this._messageStack.pop();

                switch (stackMessage.Type) {
                    case MessageBoxType.Confirmation:
                        this.showConfirmationDialog(
                            stackMessage.Message,
                            stackMessage.Callback,
                            {
                                Details: stackMessage.Details,
                                SourceID: stackMessage.SourceID
                            });
                        break;
                    case MessageBoxType.Error:
                        this.showError(stackMessage.Message, { Details: stackMessage.Details, LinkOptions: stackMessage.LinkOptions, IncludeCorrelationID: stackMessage.IncludeCorrelationID });
                        break;
                    case MessageBoxType.Notification:
                        this.showNotification(stackMessage.Message, { Details: stackMessage.Details, LinkOptions: stackMessage.LinkOptions });
                        break;
                }
            } else {
                util.changeAttribute(this._messageBoxContainer, appElements.GeneralAttributes.isShown, false);
                $(window).unbind("keydown");
                util.setFocus($("body"));
            }
        }
    }

    /** The handler for errors and confirmation messages. */
    export let handler: messageBoxHandling.MessageBoxHandler;
}
